import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Accordion, Badge, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ToastComponent } from "../../Components/ToastComponent";
import { parseOriginDestinationFromMapLink, parseLatLngString } from "../utils/googleMaps";

// #317: GET /routes/{id} foi removido do backend (confirmado pelo Reginaldo).
// Agora não buscamos mais o detalhe por id: a listagem (GET /routes) já traz
// todos os campos de cada rota, então recebemos a rota inteira por prop.

const COORDINATE_REGEX = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;


function normalizePointObject(item, idx) {
  const label =
    item?.label ?? item?.Label ?? item?.name ?? item?.Name ?? item?.address ?? item?.Address ?? null;
  const visitPointId = item?.visitPointId ?? item?.VisitPointId ?? null;
  const latitude = item?.latitude ?? item?.Latitude ?? null;
  const longitude = item?.longitude ?? item?.Longitude ?? null;
  const type = item?.type ?? item?.Type ?? null;
  return {
    id: item?.id ?? `interm_${idx}`,
    type,
    label,
    latitude,
    longitude,
    visitPointId,
  };
}


function parseIntermediatePoints(raw) {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map((item, idx) => normalizePointObject(item, idx));
  }

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item, idx) => normalizePointObject(item, idx));
      }
    } catch {
      // não é JSON — segue para o parser legado abaixo
    }

    return raw
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((chunk, idx) => {
        const match = chunk.match(COORDINATE_REGEX);
        if (match) {
          return {
            id: `interm_${idx}`,
            type: null,
            label: null,
            latitude: parseFloat(match[1]),
            longitude: parseFloat(match[2]),
            visitPointId: null,
          };
        }
        return {
          id: `interm_${idx}`,
          type: null,
          label: chunk,
          latitude: null,
          longitude: null,
          visitPointId: null,
        };
      });
  }

  return [];
}

export default function RouteDetailsModal({ show, route, handleClose }) {
  const { t } = useTranslation();
  const [toastParams, setToastParams] = useState("");
  const [geocodedLabels, setGeocodedLabels] = useState({});
  const navigate = useNavigate();

  const showToast = (title, body, color = "success") =>
    setToastParams(JSON.stringify({ show: true, title, body, color }));

  const copyToClipboard = (text, label) => {
    if (!text) {
      showToast("Aviso", `${label} não disponível para esta rota.`, "warning");
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => showToast("Sucesso", `${label} copiado.`))
      .catch(() => showToast("Erro", `Não foi possível copiar ${label.toLowerCase()}.`, "danger"));
  };

  // Campos com fallback PascalCase/camelCase, mesma lógica de antes
  const data = route;
  const mapLink = data?.mapLink ?? data?.MapLink;
  const instructions = data?.instructions ?? data?.Instructions;
  const vehicleType = data?.vehicleType ?? data?.VehicleType;


  const { originName, destinationName } = useMemo(() => {
    let origin = data?.startLocation ?? data?.StartLocation ?? data?.originName ?? data?.OriginName;
    let destination = data?.endLocation ?? data?.EndLocation ?? data?.destinationName ?? data?.DestinationName;

    if (!origin || !destination) {
      const parsed = parseOriginDestinationFromMapLink(mapLink);
      if (!origin) origin = parsed.origin;
      if (!destination) destination = parsed.destination;
    }

    return { originName: origin || null, destinationName: destination || null };
  }, [data, mapLink]);

  const intermediatePointsRaw = data?.intermediatePoints ?? data?.IntermediatePoints ?? "";

  const intermediatePoints = useMemo(
    () => parseIntermediatePoints(intermediatePointsRaw),
    [intermediatePointsRaw]
  );
  const hasIntermediatePoints = intermediatePoints.length > 0;

  // Geocodificação reversa para pontos intermediários (comportamento já existente)
  useEffect(() => {
    if (!show || !hasIntermediatePoints) return;
    const geocoder =
      typeof window !== "undefined" && window.google?.maps?.Geocoder
        ? new window.google.maps.Geocoder()
        : null;
    if (!geocoder) return;
    const pending = intermediatePoints.filter(
      (p) =>
        !p.label &&
        p.latitude != null &&
        p.longitude != null &&
        geocodedLabels[p.id] === undefined
    );
    if (pending.length === 0) return;

    pending.forEach((p) => {
      setGeocodedLabels((prev) => ({ ...prev, [p.id]: null }));
      geocoder.geocode(
        { location: { lat: p.latitude, lng: p.longitude } },
        (results, status) => {
          if (status === "OK" && results?.[0]?.formatted_address) {
            setGeocodedLabels((prev) => ({ ...prev, [p.id]: results[0].formatted_address }));
          } else {
            setGeocodedLabels((prev) => ({ ...prev, [p.id]: false }));
          }
        }
      );
    });
  }, [show, hasIntermediatePoints, intermediatePoints]);

  // Geocodificação reversa para Origem/Destino quando caírem no fallback de coordenada
  const [originDestGeocode, setOriginDestGeocode] = useState({});

  useEffect(() => {
    if (!show) return;
    const geocoder =
      typeof window !== "undefined" && window.google?.maps?.Geocoder
        ? new window.google.maps.Geocoder()
        : null;
    if (!geocoder) return;

    [originName, destinationName].forEach((value) => {
      if (!value) return;
      const coord = parseLatLngString(value);
      if (!coord || originDestGeocode[value] !== undefined) return;

      setOriginDestGeocode((prev) => ({ ...prev, [value]: null }));
      geocoder.geocode({ location: coord }, (results, status) => {
        if (status === "OK" && results?.[0]?.formatted_address) {
          setOriginDestGeocode((prev) => ({ ...prev, [value]: results[0].formatted_address }));
        } else {
          setOriginDestGeocode((prev) => ({ ...prev, [value]: false }));
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, originName, destinationName]);

  const renderOriginDest = (value) => {
    if (!value) return "—";
    const isCoord = !!parseLatLngString(value);
    if (!isCoord) return value;

    const resolved = originDestGeocode[value];
    if (resolved === undefined || resolved === null) {
      return (
        <span className="d-flex align-items-center gap-1 text-secondary">
          <Spinner animation="border" size="sm" /> {value}
        </span>
      );
    }
    return resolved === false ? value : resolved;
  };

  const typeInfo = (type) => {
    switch (type) {
      case "address":
        return { label: t("ROUTING.TYPE_ADDRESS"), variant: "primary" };
      case "visitPoint":
        return { label: t("ROUTING.TYPE_VISIT_POINT"), variant: "success" };
      case "coordinate":
        return { label: t("ROUTING.TYPE_COORDINATE"), variant: "secondary" };
      default:
        return null;
    }
  };

  const openRoutingWithVehicle = () => {
    if (!vehicleType) return;
    navigate("/routing/new", {
      state: {
        vehicleType,
      },
    });
  };

  return (
    <>
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da rota</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!data ? (
            <div className="text-secondary" style={{ fontSize: 13 }}>
              Não foi possível carregar os detalhes desta rota.
            </div>
          ) : (
            <>
              {vehicleType && (
                <p style={{ fontSize: 13 }}>
                  <strong>Veículo:</strong> {vehicleType}
                </p>
              )}
              <p style={{ fontSize: 13 }}>
                <strong>Origem:</strong> {renderOriginDest(originName)}
              </p>
              <p style={{ fontSize: 13 }}>
                <strong>Destino:</strong> {renderOriginDest(destinationName)}
              </p>

              {hasIntermediatePoints && (
                <div style={{ fontSize: 13 }} className="mb-2">
                  <strong>Pontos intermediários:</strong>
                  <ul className="list-unstyled mt-2 mb-0">
                    {intermediatePoints.map((point, idx) => {
                      const info = typeInfo(point.type);
                      const geocoded = geocodedLabels[point.id];
                      const isGeocoding = geocoded === null;
                      const coordText =
                        point.latitude != null && point.longitude != null
                          ? `${point.latitude}, ${point.longitude}`
                          : null;

                      let displayLabel = point.label;
                      if (!displayLabel) {
                        if (isGeocoding) {
                          displayLabel = null;
                        } else if (geocoded) {
                          displayLabel = geocoded;
                        } else {
                          displayLabel = coordText;
                        }
                      }

                      const showCoordAsSecondary =
                        coordText && displayLabel && displayLabel !== coordText;

                      return (
                        <li
                          key={point.id}
                          className="d-flex align-items-center gap-2 py-1"
                          style={{ borderBottom: idx < intermediatePoints.length - 1 ? "1px solid var(--border-color, #e9ecef)" : "none" }}
                        >
                          {info && <Badge bg={info.variant}>{info.label}</Badge>}
                          {isGeocoding ? (
                            <span className="d-flex align-items-center gap-1 text-secondary">
                              <Spinner animation="border" size="sm" /> {coordText}
                            </span>
                          ) : (
                            <span>
                              {displayLabel}
                              {showCoordAsSecondary && (
                                <>
                                  <br />
                                  <small className="text-secondary">{coordText}</small>
                                </>
                              )}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <Accordion className="mt-3" flush>
                <Accordion.Item eventKey="instructions">
                  <Accordion.Header>Instruções de rota</Accordion.Header>
                  <Accordion.Body style={{ fontSize: 13 }}>
                    {instructions ? (
                      <pre
                        className="mb-0"
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 220,
                          overflowY: "auto",
                        }}
                      >
                        {instructions}
                      </pre>
                    ) : (
                      <span className="text-secondary">
                        Instruções não disponíveis para esta rota.
                      </span>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className="d-flex gap-2 mt-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => copyToClipboard(mapLink, "Link")}
                >
                  Copiar link
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => copyToClipboard(instructions, "Instruções")}
                >
                  Copiar instruções
                </Button>
                {vehicleType && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={openRoutingWithVehicle}
                  >
                    Reabrir roteirização
                  </Button>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
